import React, { useEffect, useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth";
import { useDevices } from "../devices";
import DevicePicker from "../components/DevicePicker";
import { Gauge, ShieldAlert, LogOut as IgnitionIcon, MapPin, CheckCircle2, Siren, Bell } from "lucide-react";

function fmtIST(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit", month: "short",
    hour: "2-digit", minute: "2-digit", hour12: true,
    timeZone: "Asia/Kolkata",
  });
}
function iconFor(type) {
  if (type === "overspeed") return <Gauge size={18} />;
  if (type === "geofence_enter" || type === "geofence_exit") return <ShieldAlert size={18} />;
  if (type === "sos") return <Siren size={18} />;
  return <IgnitionIcon size={18} />;
}
function iconBgFor(type) {
  if (type === "overspeed") return "bg-amber-50 text-amber-600 border-amber-100";
  if (type === "geofence_exit") return "bg-red-50 text-red-500 border-red-100";
  if (type === "geofence_enter") return "bg-emerald-50 text-emerald-600 border-emerald-100";
  if (type === "sos") return "bg-red-100 text-red-600 border-red-200";
  return "bg-brand-50 text-brand-600 border-brand-100";
}
function accentBarFor(type) {
  if (type === "overspeed") return "bg-amber-400";
  if (type === "geofence_exit") return "bg-red-500";
  if (type === "geofence_enter") return "bg-emerald-500";
  if (type === "sos") return "bg-red-600";
  return "bg-brand-400";
}
function labelFor(type, t) {
  if (type === "overspeed") return t.overspeed;
  if (type === "geofence_enter") return t.geofenceEnter;
  if (type === "geofence_exit") return t.geofenceExit;
  if (type === "sos") return t.sos;
  return t.ignition;
}

function AlertSkeleton() {
  return (
    <div className="px-4 pt-3 space-y-2.5">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-white border border-surface-border rounded-2xl p-4 animate-pulse overflow-hidden relative">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-surface-muted" />
          <div className="pl-3 flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-surface-muted shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-surface-muted rounded-lg w-2/5" />
              <div className="h-3 bg-surface-muted rounded-lg w-4/5" />
              <div className="h-3 bg-surface-muted rounded-lg w-1/3" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Alerts() {
  const { t } = useAuth();
  const { selectedDeviceId } = useDevices();
  const deviceId = selectedDeviceId;
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async (id) => {
    const { data } = await api.get(`/alerts/${id}`);
    setAlerts(data);
  };

  useEffect(() => {
    if (!deviceId) { setAlerts([]); setLoading(false); return; }
    let iv;
    setLoading(true);
    (async () => {
      try {
        await load(deviceId);
        iv = setInterval(() => load(deviceId), 15000);
      } finally { setLoading(false); }
    })();
    return () => { if (iv) clearInterval(iv); };
  }, [deviceId]);

  const markRead = async (id) => {
    await api.post(`/alerts/${id}/read`);
    setAlerts((a) => a.map((x) => (x.id === id ? { ...x, read: true } : x)));
  };

  const filtered = alerts.filter((a) => (filter === "unread" ? !a.read : true));
  const unreadCount = alerts.filter((a) => !a.read).length;

  return (
    <div data-testid="alerts-screen" className="min-h-[100dvh] bg-surface-soft pb-28">

      {/* Sticky frosted header */}
      <div className="screen-header px-5 pt-6 pb-4">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h1 className="text-[22px] font-heading font-black text-ink">{t.alerts}</h1>
            {unreadCount > 0 && (
              <p className="text-[12px] text-ink-muted mt-0.5 font-body">
                {unreadCount} {t.unread}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <DevicePicker />
            {unreadCount > 0 && (
              <span data-testid="unread-count" className="bg-brand-600 text-white text-[11px] font-bold px-2.5 py-1 rounded-full shadow-brand">
                {unreadCount}
              </span>
            )}
          </div>
        </div>

        <div className="mt-3 flex gap-2">
          {["all", "unread"].map((k) => (
            <button
              key={k}
              data-testid={`filter-${k}`}
              onClick={() => setFilter(k)}
              className={`px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all duration-200 ${
                filter === k
                  ? "bg-brand-600 text-white shadow-brand"
                  : "bg-white border border-surface-border text-ink-muted hover:text-ink hover:border-ink-subtle"
              }`}
            >
              {k === "all" ? t.allAlerts : t.unread}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <AlertSkeleton />
      ) : filtered.length === 0 ? (
        <div data-testid="alerts-empty" className="flex flex-col items-center justify-center py-20 text-center px-6">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-tr from-brand-500/10 to-accent-cyan/10 flex items-center justify-center border border-brand-500/10 mb-5">
            <Bell size={32} className="text-brand-400" />
          </div>
          <h3 className="text-[16px] font-heading font-bold text-ink">{t.noAlerts}</h3>
          <p className="text-[13px] text-ink-soft mt-2 max-w-[240px] leading-relaxed">
            Alerts for geofence, overspeed, and SOS events will appear here.
          </p>
        </div>
      ) : (
        <ul data-testid="alerts-list" className="px-4 pt-3 space-y-2.5">
          {filtered.map((a) => (
            <li
              key={a.id}
              data-testid={`alert-item-${a.id}`}
              onClick={() => !a.read && markRead(a.id)}
              className={`cursor-pointer ${a.read ? "" : "cursor-pointer"}`}
            >
              <div className={`relative bg-white border border-surface-border rounded-2xl overflow-hidden transition-all duration-200 ${
                a.read
                  ? "opacity-75"
                  : "hover:border-brand-200/60 hover:shadow-card-md"
              }`}>
                {/* Color accent left bar */}
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${accentBarFor(a.type)}`} />

                <div className="pl-4 pr-4 py-3.5 flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${iconBgFor(a.type)}`}>
                    {iconFor(a.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-[13px] ${a.read ? "font-medium text-ink-soft" : "font-extrabold text-ink"}`}>
                        {labelFor(a.type, t)}
                      </span>
                      {!a.read && (
                        <span className="w-2 h-2 rounded-full bg-brand-600 shrink-0" />
                      )}
                    </div>
                    <div className={`text-[12px] mt-0.5 leading-relaxed ${a.read ? "text-ink-muted" : "text-ink-soft font-medium"}`}>
                      {a.message}
                    </div>
                    <div className="text-[11px] text-ink-muted mt-1.5 inline-flex items-center gap-1">
                      <MapPin size={10} />
                      {a.latitude?.toFixed(4)}, {a.longitude?.toFixed(4)} · {fmtIST(a.timestamp)}
                    </div>
                  </div>
                  {a.read && (
                    <CheckCircle2 size={15} className="text-ink-subtle mt-0.5 shrink-0" />
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
